import {
  QUERY_REQUEST_SENT,
  MUTATION_REQUEST_SENT,
  QUERY_REQUIRED,
  QUERY_NOT_REQUIRED,
  MUTATION_REQUIRED,
  QUERY_RESPONSE_RECEIVED,
  MUTATION_RESPONSE_RECEIVED,
} from '@web-platform/shared-universal-constants/src/network';

import type { RequestInit } from '@web-platform/shared-universal-types/src/network/internals/types';
import type {
  QueryType,
  MutationType,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import type { Variables } from '@web-platform/shared-universal-types/src/network/types';
import type {
  MutationRequestAction,
  MutationRequiredAction,
  MutationRequiredOptions,
  QueryNotRequiredAction,
  QueryRequestAction,
  QueryRequiredAction,
  QueryResponseAction,
  MutationResponseAction,
  PayloadResponse,
} from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type { NextRequestAt } from '@web-platform/shared-universal-types/src/redux/actions/nextRequestAt';

export function queryRequestSent(type: QueryType, variables: Variables): QueryRequestAction {
  return {
    type: QUERY_REQUEST_SENT,
    payload: {
      type,
      variables,
      now: Date.now(),
    },
  };
}

export function queryRequired(
  type: QueryType,
  variables: Variables,
  request: RequestInit,
  nextRequestAt: NextRequestAt,
): QueryRequiredAction {
  return {
    type: QUERY_REQUIRED,
    payload: {
      type,
      variables,
      request,
      nextRequestAt,
      now: Date.now(),
    },
  };
}

export function queryNotRequired(
  type: QueryType,
  variables: Variables,
  nextRequestAt: NextRequestAt,
): QueryNotRequiredAction {
  return {
    type: QUERY_NOT_REQUIRED,
    payload: {
      type,
      variables,
      nextRequestAt,
    },
  };
}

export function queryResponseReceived(
  type: QueryType,
  variables: Variables,
  response: PayloadResponse,
): QueryResponseAction {
  return {
    type: QUERY_RESPONSE_RECEIVED,
    error: response.error,
    payload: {
      type,
      variables,
      // @ts-expect-error: FIXME: Inconsistencies between `QueryItem` and `QueryResponseAction`, [SHIBA-891]
      response,
      now: Date.now(),
    },
  };
}

export function mutationRequestSent(id: string, type: MutationType): MutationRequestAction {
  return {
    type: MUTATION_REQUEST_SENT,
    payload: {
      id,
      type,
    },
  };
}

// As in one render pass multiple components could dispatch a call for the same mutation, we give the
// ability for a component to specify { dedupe: true } so that not more than one of that { type, variables }
// pair will be allowed to be in existence where that mutation has not yet been picked up by the network layer
// and changed to inFlight: true or beyond.
export function mutationRequired(
  type: MutationType,
  variables: Variables,
  request: RequestInit,
  options?: MutationRequiredOptions,
  now?: number,
): MutationRequiredAction {
  return {
    type: MUTATION_REQUIRED,
    payload: {
      type,
      variables,
      request,
      now: now ?? Date.now(),
      options,
    },
  };
}

export function mutationResponseReceived(
  id: string,
  type: MutationType,
  variables: Variables,
  response: PayloadResponse,
): MutationResponseAction {
  return {
    type: MUTATION_RESPONSE_RECEIVED,
    error: response.error,
    payload: {
      id,
      type,
      // @ts-expect-error: FIXME: Inconsistencies between `QueryItem` and `MutationResponseActionPayload`, [SHIBA-891]
      response,
      variables,
      now: Date.now(),
    },
  };
}
